import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Tooltip,
  Box,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import editIcon from 'assets/images/16px/edit@1x.svg'
import { compose } from 'redux'
import { withRouter, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { SubLink } from 'containers'
import TeaserHeader from 'components/Teaser/TeaserHeader'
import AddTaskGroupDialog from 'containers/TaskManagement/AddTaskGroupDialog'
import { formatCostumDateMonthNoForUI } from 'utils/formatDate'
import { Translate } from 'react-localize-redux'
import { Collapsible } from 'components'
import useTeamFeature from 'hooks/useTeamFeature'
import PropTypes from 'prop-types'
import { PlayersLoader } from 'loaders'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import UpgradeMembershipOverlay from 'components/UpgradeMembership/UpgradeMembershipOverlay'
import { fetchTaskGroup, fetchTaskGroupTasks } from 'api/taskGroups/api'
import { TASK_SERIES_DUE_DATES } from '../../../constants/taskGroups'
import TasksTable from './TasksTable'
import { TEAM_PLAYER_DASHBOARD_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const TaskGroupContainer = ({ taskGroupUuid, players, dateFormat }) => {
  const [taskGroup, setTaskGroup] = useState(null)
  const [assignees, setAssignees] = useState([])
  const [open, setOpen] = useState(false)
  const [taskGroupState, setTaskGroupState] = useState(null)

  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasTaskGroupManagerEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.task_group_export_enabled
  )
  const { enabled: hasShowTeaserHeaderEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.teasers_enabled
  )
  const { enabled: hasAddTaskQuestionsEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.task_questions_enabled
  )

  const handleOpen = () => {
    if (featureGuard(hasTaskGroupManagerEnabled)) return

    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }
  const dataValue = useLocation()

  const loadTaskGroup = async () => {
    try {
      const res = await fetchTaskGroup(taskGroupUuid)
      setTaskGroup(res.data)
    } catch (error) {
      console.error('Error fetching task group:', error)
    }
  }

  useEffect(() => {
    loadTaskGroup()
  }, [taskGroupUuid])

  useEffect(() => {
    if (taskGroup) {
      const tempAssignees = players.filter((player) =>
        taskGroup.person_uuids.includes(player.uuid)
      )
      fetchTaskGroupTasks(taskGroup.uuid).then((res) => {
        const { data } = res
        setTaskGroupState({
          ...taskGroup,
          events: taskGroup?.relationships?.events?.map((event) => {
            const tasks = data.filter(
              (d) => d.relationships?.event?.uuid === event.uuid
            )

            return {
              ...event,
              tasks,
            }
          }),
        })
        setAssignees(
          tempAssignees.map((player) => {
            return {
              ...player,
              tasks: data.find((d) => d.person_uuid === player.uuid),
            }
          })
        )
      })
    }
  }, [taskGroup, players])

  return (
    <Box style={{ position: 'relative' }}>
      {taskGroup && (
        <>
          {!hasTaskGroupManagerEnabled && <UpgradeMembershipOverlay />}
          <PlayersLoader>
        {hasShowTeaserHeaderEnabled && (
          <Grid item xs={12} md={12} lg={12}>
            <TeaserHeader />
          </Grid>
        )}

        <Grid container spacing={2}>
          {open && (
            <AddTaskGroupDialog
              open={open}
              onClose={handleClose}
              onSuccess={loadTaskGroup}
              selectedTaskGroup={taskGroupState}
            />
          )}
          <Grid item xs={12} sm={8}>
            <Card>
              <CardHeader
                title={
                  <Typography variant="h4" component="h4">
                    <Translate id="task-management.feedbacks-title" />
                  </Typography>
                }
              />
              <CardContent>
                {taskGroup?.event_type?.toLowerCase().includes('event')
                  ? taskGroupState?.events?.map((event) => (
                      <Collapsible
                        title={
                          <>
                            <Translate id="task-management.task-date" />
                            {` ${formatCostumDateMonthNoForUI(
                              event.created_at,
                              dateFormat
                            )}`}
                          </>
                        }
                        defaultIsActive={`#${event.uuid}` === dataValue.hash}
                        key={event.uuid}
                        eventId={event.uuid}
                      >
                        <TasksTable
                          taskGroup={taskGroupState}
                          event={event}
                          dateFormat={dateFormat}
                        />
                      </Collapsible>
                    ))
                  : taskGroupState?.events?.map((event) => (
                      <Collapsible
                        title={
                          event.event_type?.toLowerCase() === 'training' ? (
                            <>
                              <Translate id="menu.training" />{' '}
                              <Translate id="task-management.from-small" />
                              {` ${formatCostumDateMonthNoForUI(
                                event.date,
                                dateFormat
                              )}. `}
                              <Translate id="task-management.task-due-date" />
                              {` ${formatCostumDateMonthNoForUI(
                                event.due_date,
                                dateFormat
                              )}`}
                            </>
                          ) : (
                            <>
                              <Translate id="menu.match-day" />
                              {` vs ${event.opponent} `}
                              <Translate id="task-management.from-small" />
                              {`  ${formatCostumDateMonthNoForUI(
                                event.date,
                                dateFormat
                              )}. `}
                              <Translate id="task-management.task-due-date" />
                              {` ${formatCostumDateMonthNoForUI(
                                event.due_date,
                                dateFormat
                              )}`}
                            </>
                          )
                        }
                        defaultIsActive={`#${event.uuid}` === dataValue.hash}
                        key={event.uuid}
                        eventId={event.uuid}
                      >
                        <TasksTable
                          taskGroup={taskGroupState}
                          event={event}
                          dateFormat={dateFormat}
                        />
                      </Collapsible>
                    ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardHeader
                title={
                  <Typography variant="h4">
                    <Translate id="task-management.task-details" />
                  </Typography>
                }
                action={
                  !taskGroup.completed && (
                    <Tooltip
                      title={<Translate id="button.edit" />}
                      placement="top"
                      arrow
                    >
                      <IconButton onClick={handleOpen}>
                        <img src={editIcon} alt={editIcon} />
                      </IconButton>
                    </Tooltip>
                  )
                }
              />
              <CardContent>
                <TableContainer>
                  <Table aria-label="tasks table">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Translate id="articles.title" />
                        </TableCell>
                        <TableCell align="right">{taskGroup.title}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Translate id="task-management.assignees" />
                        </TableCell>
                        <TableCell align="right">
                          {assignees.map((pl) => (
                            <SubLink
                              to={TEAM_PLAYER_DASHBOARD_ROUTE.populateURLParams(
                                {
                                  playerId: pl.id,
                                }
                              )}
                              key={pl.id}
                            >
                              @{pl.first_name} &nbsp;
                            </SubLink>
                          ))}
                        </TableCell>
                      </TableRow>
                      {taskGroup?.event_type
                        ?.toLowerCase()
                        .includes('event') && (
                        <TableRow>
                          <TableCell>
                            <Translate id="task-management.due-date" />
                          </TableCell>
                          <TableCell align="right">
                            {taskGroup.start_date && taskGroup.end_date
                              ? TASK_SERIES_DUE_DATES.find(
                                  (p) =>
                                    p.value ===
                                    taskGroup.due_date_offset?.toString()
                                )?.title
                              : formatCostumDateMonthNoForUI(
                                  taskGroup?.relationships?.events[0]?.due_date,
                                  dateFormat
                                )}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell>
                          <Translate id="drills.description" />
                        </TableCell>
                        <TableCell align="right">
                          {taskGroup.description}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
            {hasAddTaskQuestionsEnabled &&
              taskGroup.relationships?.questions?.length > 0 && (
              <Card style={{ marginTop: '0.20rem' }}>
                <CardHeader
                  title={
                    <Typography variant="h4">
                      <Translate id="task-management.questions-title" />
                    </Typography>
                  }
                />
                <CardContent>
                  {taskGroup.relationships.questions.map((element) => (
                    <Typography
                      key={element.uuid}
                      variant="h4"
                      className="mb-3"
                    >
                      {element.question}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            )}
          </Grid>
          </Grid>
        </PlayersLoader>
        </>
      )}
    </Box>
  )
}
TaskGroupContainer.propTypes = {
  taskGroupUuid: PropTypes.string.isRequired,
  players: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  dateFormat: PropTypes.string.isRequired,
}
export default compose(
  withRouter,
  connect(({ players, auth }) => ({
    players: players.items,
    dateFormat: auth.user.datetime_format,
  }))
)(TaskGroupContainer)
